import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable, from, map, mergeMap, of, switchMap, toArray } from 'rxjs';
import { ApiClientService } from './api-client.service';
import { Repository } from './repository.interface';
import { QueryParams, PaginatedResponse } from '@core/models/api.model';

/** Rows pulled per request when auto-paginating a full list. */
const DEFAULT_PAGE_SIZE = 200;
/**
 * Hard ceiling on the number of pages auto-fetched for a list view, so a very
 * large table can never trigger a runaway fan-out of requests. With the default
 * page size this caps a list at 10,000 rows; beyond that the grid still renders
 * the first 10,000 and paginates them client-side.
 */
const MAX_AUTO_PAGES = 50;
const MAX_CONCURRENT_PAGES = 4;

@Injectable({ providedIn: 'root' })
export class ApiRepository<T extends { id: string }> implements Repository<T> {
  protected api = inject(ApiClientService);
  protected basePath = '';

  /**
   * Fetches every row for a list view. The API is paginated, so this requests
   * the first page, then (if the reported total exceeds what came back) fetches
   * the remaining pages and concatenates them. This prevents list pages from
   * silently truncating at the API's default page size when a module grows
   * beyond it, while the client-side grid continues to paginate the full set.
   */
  getAll(params?: QueryParams): Observable<T[]> {
    return this.getAllFrom<T>(this.basePath, params);
  }

  /**
   * Same auto-pagination as {@link getAll}, but against an arbitrary path and row shape, so a
   * subclass can consume a compact list or lookup contract without duplicating the paging logic.
   * Pass `context` (for example `skipLoading()`) when the read is a secondary, non-blocking one
   * that should not hold up the global busy overlay.
   */
  protected getAllFrom<R>(path: string, params?: QueryParams, context?: HttpContext): Observable<R[]> {
    const pageSize = params?.pageSize && params.pageSize > 0 ? params.pageSize : DEFAULT_PAGE_SIZE;
    const baseParams: QueryParams = { ...params, pageSize };

    return this.fetchPageFrom<R>(path, baseParams, 1, context).pipe(
      switchMap(first => {
        const data = first.data ?? [];
        const total = first.total ?? data.length;
        // Single page already holds everything (or there is nothing more to load).
        if (data.length === 0 || data.length >= total) {
          return of(data);
        }

        const totalPages = Math.min(Math.ceil(total / pageSize), MAX_AUTO_PAGES);
        const remaining = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        return from(remaining).pipe(
          mergeMap(page => this.fetchPageFrom<R>(path, baseParams, page, context).pipe(
            map(response => ({ page, rows: response.data ?? [] }))
          ), MAX_CONCURRENT_PAGES),
          toArray(),
          map(pages => data.concat(...pages.sort((left, right) => left.page - right.page).map(page => page.rows)))
        );
      })
    );
  }

  /** Requests a single page and guards against a missing/partial response payload. */
  private fetchPageFrom<R>(
    path: string,
    params: QueryParams,
    page: number,
    context?: HttpContext
  ): Observable<PaginatedResponse<R>> {
    const query = this.buildQuery({ ...params, page });
    return this.api.get<PaginatedResponse<R>>(`${path}${query}`, context).pipe(
      map(response => response ?? { data: [], total: 0, page, pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE })
    );
  }

  getById(id: string, context?: HttpContext): Observable<T | undefined> {
    return this.api.get<T | undefined>(`${this.basePath}/${id}`, context);
  }

  create(item: Partial<T>): Observable<T> {
    return this.api.post<T>(this.basePath, item);
  }

  update(id: string, item: Partial<T>): Observable<T> {
    return this.api.put<T>(`${this.basePath}/${id}`, item);
  }

  delete(id: string): Observable<void> {
    return this.api.delete<void>(`${this.basePath}/${id}`);
  }

  private buildQuery(params: QueryParams): string {
    const parts: string[] = [];
    if (params.page) parts.push(`page=${params.page}`);
    if (params.pageSize) parts.push(`pageSize=${params.pageSize}`);
    if (params.sortBy) parts.push(`sortBy=${params.sortBy}`);
    if (params.sortDirection) parts.push(`sortDirection=${params.sortDirection}`);
    if (params.search) parts.push(`search=${encodeURIComponent(params.search)}`);
    return parts.length ? `?${parts.join('&')}` : '';
  }
}
